import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeftIcon, SaveIcon, UserPlusIcon } from 'lucide-react'
import React, { useContext, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import NeedsAttentionContext from '../../context/NeedsAttentionContext'
import { type GrapeAPIErrorType } from '../../hooks/GrapeAPIError'
import { useCreateOrEditTag } from '../../hooks/needs_attention/useCreateOrEditTag'
import { getTagsForBrokerageQueryKey } from '../../hooks/needs_attention/useGetTagsForBrokerage'
import { Banner } from '../../reusable/Banner'
import {
  FormCheckboxField,
  FormTextArea,
  FormTextField,
} from '../../reusable/form/FormFields'
import { Button } from '../../ui/button'
import { type Tag } from '../types'

type CreateOrEditTagProps = {
  tag?: Tag
  setIsOpen: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean
      tag: Tag | undefined
    }>
  >
}

type CreateOrEditTagInput = {
  category: string
  name: string
  description?: string
  temporary: boolean
}

const CreateOrEditTag = ({ tag, setIsOpen }: CreateOrEditTagProps) => {
  const queryClient = useQueryClient()
  const { isNAVAdmin } = useContext(NeedsAttentionContext)

  const defaultValues: CreateOrEditTagInput = {
    category: tag?.category || '',
    name: tag?.name || '',
    description: tag?.description || '',
    temporary: !!tag?.brokerage_id || true,
  }

  const {
    register,
    handleSubmit,
    setValue,
    formState,
    formState: { errors },
    reset,
  } = useForm<CreateOrEditTagInput>({
    defaultValues,
  })

  useEffect(() => {
    if (tag) {
      setValue('category', tag.category)
      setValue('name', tag.name)
      setValue('description', tag.description)
      setValue('temporary', !tag.brokerage_id)
    }
  }, [tag])

  const [error, setError] = React.useState<GrapeAPIErrorType | undefined>(
    undefined
  )

  const isCreate = !tag
  const createOrEditTagMutation = useCreateOrEditTag({ isCreate })

  const onSubmit: SubmitHandler<CreateOrEditTagInput> = async (data) => {
    createOrEditTagMutation.mutate(
      { id: tag?.id, ...data },
      {
        onSuccess: () => {
          // TODO: Investigate updating the cache instead of invalidating the query.
          queryClient.invalidateQueries({
            queryKey: getTagsForBrokerageQueryKey(),
          })
          setIsOpen({ isOpen: false, tag: undefined })
          reset()
        },
        onError: (error) => {
          setError(error)
          console.error(error)
        },
      }
    )
  }

  const isSubmitDisabled = formState.isSubmitting || !formState.isValid

  return (
    <div className='space-y-4'>
      <div
        className='flex content-center text-indigo-500 text-sm cursor-pointer pb-4 w-48'
        onClick={() => setIsOpen({ isOpen: false, tag: undefined })}
      >
        <ArrowLeftIcon className='self-center' size={16} />
        <span className='pl-2'>Back</span>
      </div>
      <div className='font-semibold'>{isCreate ? 'Create' : 'Edit'} tag</div>
      {error && (
        <Banner type='critical' titleText={error.message || 'Error creating tag'}>
          {error.errors?.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </Banner>
      )}
      {Object.keys(errors).length !== 0 && <div>errors</div>}
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
        <div>
          <FormTextField
            fieldId='category'
            fieldLabel='Category'
            register={register}
            validationRule={{ required: 'Category is required' }}
          />
          <div className='text-sm font-light mt-1 p-1'>
            Category must be lowercase letters or numbers separated by
            underscores
          </div>
        </div>

        <div>
          <FormTextField
            fieldId='name'
            fieldLabel='Name'
            register={register}
            validationRule={{ required: 'Name is required' }}
          />
          <div className='text-sm font-light mt-1 p-1'>
            Display name of the tag
          </div>
        </div>
        <div>
          <FormTextArea
            fieldId='description'
            fieldLabel='Description'
            register={register}
          />
          <div className='text-sm font-light mt-1 p-1'>
            Please provide some helpful context to ensure team members use this
            tag appropriately!
          </div>
        </div>
        {/*
          If user is an NAV admin, allow them to create permanent tags if desired. These will be tags shared across brokerages
          for common issues that need to be tracked. Temporary tags are only visible to the brokerage that created them.
        */}
        {isNAVAdmin && (
          <div>
            <FormCheckboxField
              fieldId='temporary'
              fieldLabel='Temporary'
              register={register}
              disabled={!isNAVAdmin}
            />
          </div>
        )}
        <footer className='mt-4'>
          <Button type='submit' variant='primary' disabled={isSubmitDisabled}>
            {isCreate
              ? (
                <UserPlusIcon size={21} className='mr-2' />
                )
              : (
                <SaveIcon size={21} className='mr-2' />
                )}
            {isCreate ? 'Create tag' : 'Save changes'}
          </Button>
        </footer>
      </form>
    </div>
  )
}

export default CreateOrEditTag
